import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Eye, Trash2, Check, Inbox, Clock, CheckCircle, Reply, MoreHorizontal, MessageSquare, X, Loader2 } from "lucide-react"
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Checkbox } from "@/components/ui/checkbox"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"

import { useContact } from '@/hooks/use-forms'
import { useAuthStore } from '@/stores/authStore'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

export default function IletisimMesajlari() {
  const user = useAuthStore((s) => s.auth.user)
  const isEditor = user?.role?.includes('editor') && !user?.role?.includes('admin') && !user?.role?.includes('super_admin')

  const { data: messages = [], isLoading, updateStatus, deleteMessage } = useContact()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedMessages, setSelectedMessages] = useState<string[]>([])
  const [viewMessage, setViewMessage] = useState<any | null>(null)
  const [replyDialogOpen, setReplyDialogOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null)

  const filteredMessages = useMemo(() => {
    let result = messages

    if (searchTerm) {
      result = result.filter((msg: any) =>
        msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.message.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      result = result.filter((msg: any) =>
        msg.status.toLowerCase() === statusFilter.toLowerCase()
      )
    }

    return result
  }, [messages, searchTerm, statusFilter])

  const toggleSelectAll = () => {
    if (selectedMessages.length === filteredMessages.length) {
      setSelectedMessages([])
    } else {
      setSelectedMessages(filteredMessages.map((m: any) => m.id))
    }
  }

  const toggleSelectMessage = (id: string) => {
    setSelectedMessages(current =>
      current.includes(id)
        ? current.filter(msgId => msgId !== id)
        : [...current, id]
    )
  }

  const handleMarkAsRead = async (id: string) => {
    await updateStatus({ id, status: 'resolved' }) // Assuming resolved means read/processed
  }

  const handleReply = async () => {
    if (viewMessage) {
      await updateStatus({ id: viewMessage.id, status: 'in_progress' })
      toast.success('Yanıt gönderildi (Simüle edildi)')
      setReplyDialogOpen(false)
      setReplyText('')
      setViewMessage(null)
    }
  }

  const handleDelete = (id: string) => {
    setMessageToDelete(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (messageToDelete) {
      await deleteMessage(messageToDelete)
      setDeleteDialogOpen(false)
      setMessageToDelete(null)
    }
  }

  const confirmBulkDelete = async () => {
    for (const id of selectedMessages) {
      await deleteMessage(id)
    }
    setSelectedMessages([])
    setBulkDeleteDialogOpen(false)
  }

  const handleBulkMarkAsRead = async () => {
    for (const id of selectedMessages) {
      await updateStatus({ id, status: 'resolved' })
    }
    setSelectedMessages([])
  }

  const stats = {
    total: messages.length,
    read: messages.filter((m: any) => m.status === 'resolved').length,
    pending: messages.filter((m: any) => m.status === 'new').length,
    replied: messages.filter((m: any) => m.status === 'in_progress').length
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">İletişim Mesajları</h1>
            <p className="text-sm text-muted-foreground">Gelen mesajları görüntüleyin ve yönetin</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Toplam Mesaj</CardTitle>
              <Inbox className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">+5 bu hafta</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Okundu</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.read}</div>
              <p className="text-xs text-muted-foreground">{Math.round(stats.read / stats.total * 100)}% okundu</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Bekliyor</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending}</div>
              <p className="text-xs text-muted-foreground">Yanıt bekliyor</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Cevaplandı</CardTitle>
              <Reply className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.replied}</div>
              <p className="text-xs text-muted-foreground">Ort. 2 saat</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <CardTitle>Gelen Kutusu</CardTitle>
                {!isEditor && selectedMessages.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{selectedMessages.length} seçili</span>
                    <Button variant="outline" size="sm" onClick={handleBulkMarkAsRead}>
                      <Check className="mr-2 h-4 w-4" /> Okundu
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setBulkDeleteDialogOpen(true)}>
                      <Trash2 className="mr-2 h-4 w-4" /> Sil
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="new">Bekliyor</SelectItem>
                    <SelectItem value="resolved">Okundu</SelectItem>
                    <SelectItem value="in_progress">Cevaplandı</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input placeholder="Mesaj ara..." className="w-full sm:w-[200px] pr-8" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? null : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox checked={selectedMessages.length === filteredMessages.length && filteredMessages.length > 0} onCheckedChange={toggleSelectAll} />
                      </TableHead>
                      <TableHead>Gönderen</TableHead>
                      <TableHead>Konu</TableHead>
                      <TableHead>Öncelik</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMessages.map((msg: any) => (
                      <TableRow key={msg.id}>
                        <TableCell>
                          <Checkbox checked={selectedMessages.includes(msg.id)} onCheckedChange={() => toggleSelectMessage(msg.id)} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{msg.name.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-medium">{msg.name}</span>
                              <span className="text-xs text-muted-foreground">{msg.email}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{msg.subject}</span>
                            <span className="text-xs text-muted-foreground max-w-[200px] truncate">{msg.message}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={(msg as any).priority === "Yüksek" ? "destructive" : (msg as any).priority === "Orta" ? "default" : "secondary"}>
                            {(msg as any).priority || 'Normal'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={msg.status === "resolved" ? "default" : msg.status === "in_progress" ? "outline" : "destructive"}>
                            {msg.status === 'resolved' ? 'Okundu' : msg.status === 'in_progress' ? 'Cevaplandı' : 'Bekliyor'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{new Date(msg.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setViewMessage(msg); handleMarkAsRead(msg.id) }}>
                                <Eye className="mr-2 h-4 w-4" /> Görüntüle
                              </DropdownMenuItem>
                              {!isEditor && (
                                <>
                                  <DropdownMenuItem onClick={() => { setViewMessage(msg); setReplyDialogOpen(true) }}>
                                    <Reply className="mr-2 h-4 w-4" /> Yanıtla
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleMarkAsRead(msg.id)}>
                                    <Check className="mr-2 h-4 w-4" /> Okundu İşaretle
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(msg.id)}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Sil
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Main>

      {/* View/Reply Dialog */}
      <Dialog open={!!viewMessage && !replyDialogOpen} onOpenChange={() => setViewMessage(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {viewMessage?.subject}
            </DialogTitle>
            <DialogDescription>Mesaj detayları</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="text-lg">{viewMessage?.name.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold">{viewMessage?.name}</h3>
                <p className="text-sm text-muted-foreground">{viewMessage?.email}</p>
                <p className="text-sm text-muted-foreground">{viewMessage?.phone}</p>
              </div>
              <div className="ml-auto">
                <Badge variant={viewMessage?.priority === "Yüksek" ? "destructive" : viewMessage?.priority === "Orta" ? "default" : "secondary"}>
                  {viewMessage?.priority}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Mesaj</label>
              <p className="mt-1 p-3 bg-muted rounded-md">{viewMessage?.message}</p>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>Tarih: {viewMessage?.createdAt ? new Date(viewMessage.createdAt).toLocaleString() : 'N/A'}</span>
              <span>Durum: <Badge variant={viewMessage?.status === "resolved" ? "default" : viewMessage?.status === "in_progress" ? "outline" : "destructive"}>{viewMessage?.status === 'resolved' ? 'Okundu' : viewMessage?.status === 'in_progress' ? 'Cevaplandı' : 'Bekliyor'}</Badge></span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewMessage(null)}>Kapat</Button>
            {!isEditor && (
              <Button onClick={() => setReplyDialogOpen(true)}><Reply className="mr-2 h-4 w-4" /> Yanıtla</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Yanıt Gönder</DialogTitle>
            <DialogDescription>{viewMessage?.name} adlı kullanıcıya yanıt yazın</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Konu</label>
              <Input value={`Re: ${viewMessage?.subject}`} readOnly />
            </div>
            <div>
              <label className="text-sm font-medium">Yanıtınız</label>
              <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Yanıtınızı yazın..." rows={5} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>İptal</Button>
            <Button onClick={handleReply} disabled={!replyText.trim()}><Reply className="mr-2 h-4 w-4" /> Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mesajı Sil</DialogTitle>
            <DialogDescription>Bu mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seçili Mesajları Sil</DialogTitle>
            <DialogDescription>{selectedMessages.length} mesajı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={confirmBulkDelete}><Trash2 className="mr-2 h-4 w-4" /> Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
